import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Combobox } from '../ui/combobox';
import { Plus, Edit, Trash2, Eye, Search, Filter, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import apiClient from '../../utils/api';
import { toast } from 'sonner';
import EnhancedCustomerModal from './EnhancedCustomerModal';

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    rateList: '',
  });
  const [columnFilters, setColumnFilters] = useState({ cred: '', customer: '', rateList: '', contacts: '', addresses: '' });
  const [filterOptions, setFilterOptions] = useState({
    rateLists: [],
  });

  useEffect(() => {
    fetchCustomers();
  }, [searchQuery]);

  useEffect(() => {
    applyFilters();
  }, [filters, allCustomers]);

  const fetchCustomers = async () => {
    try {
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
      const response = await apiClient.get(`/customers${params}`);
      const customersData = response.data;
      setAllCustomers(customersData);
      
      // Extract unique rate lists
      const rateLists = [...new Set(customersData.map(c => c.rate_list).filter(Boolean))];
      setFilterOptions({ rateLists });
    } catch (error) {
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allCustomers];
    
    if (filters.rateList) {
      filtered = filtered.filter(c => c.rate_list === filters.rateList);
    }
    
    setCustomers(filtered);
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setShowModal(true);
  };

  const handleView = (customer) => {
    setViewingCustomer(customer);
    setShowViewModal(true);
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;

    try {
      await apiClient.delete(`/customers/${customerId}`);
      toast.success('Customer deleted successfully');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setEditingCustomer(null);
    fetchCustomers();
  };

  const clearFilters = () => {
    setFilters({ rateList: '' });
    setSearchQuery('');
  };

  const hasActiveFilters = filters.rateList !== '' || searchQuery !== '';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-primary font-semibold text-slate-900">Customer Management</h2>
        <Button
          data-testid="add-customer-button"
          onClick={() => setShowModal(true)}
          size="sm"
          className="h-7 px-2 text-xs flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Customer
        </Button>
      </div>

      <Card className="p-3 bg-white rounded-lg border border-slate-200">
        {loading ? (
          <div className="text-center py-8 text-slate-600">Loading customers...</div>
        ) : customers.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            {hasActiveFilters ? 'No customers match your filters' : 'No customers found'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                    Credibility Rating
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                    Customer Name
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                    Rate List
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                    Contacts
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                    Addresses
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                    Actions
                  </th>
                </tr>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-2 py-1.5"><Input value={columnFilters.cred} onChange={(e) => setColumnFilters(f => ({ ...f, cred: e.target.value }))} placeholder="Filter..." className="h-7 text-xs" data-testid="cust-filter-cred" /></th>
                  <th className="px-2 py-1.5"><Input value={columnFilters.customer} onChange={(e) => setColumnFilters(f => ({ ...f, customer: e.target.value }))} placeholder="Filter..." className="h-7 text-xs" data-testid="cust-filter-name" /></th>
                  <th className="px-2 py-1.5"><Input value={columnFilters.rateList} onChange={(e) => setColumnFilters(f => ({ ...f, rateList: e.target.value }))} placeholder="Filter..." className="h-7 text-xs" data-testid="cust-filter-ratelist" /></th>
                  <th className="px-2 py-1.5"><Input value={columnFilters.contacts} onChange={(e) => setColumnFilters(f => ({ ...f, contacts: e.target.value }))} placeholder="Count..." className="h-7 text-xs text-center" data-testid="cust-filter-contacts" /></th>
                  <th className="px-2 py-1.5"><Input value={columnFilters.addresses} onChange={(e) => setColumnFilters(f => ({ ...f, addresses: e.target.value }))} placeholder="Count..." className="h-7 text-xs text-center" data-testid="cust-filter-addresses" /></th>
                  <th className="px-2 py-1.5">
                    {(columnFilters.cred || columnFilters.customer || columnFilters.rateList || columnFilters.contacts || columnFilters.addresses) && (
                      <Button variant="ghost" size="sm" onClick={() => setColumnFilters({ cred: '', customer: '', rateList: '', contacts: '', addresses: '' })} className="h-7 px-2 text-xs" data-testid="cust-filter-clear">
                        <X className="h-3.5 w-3.5 mr-1" />Clear
                      </Button>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {customers.filter((customer) => {
                  const displayName = customer.customer_name || customer.addresses?.[0]?.customer_name_child || customer.parent_customer_name || '';
                  if (columnFilters.cred && !String(customer.credibility_rating || '').toLowerCase().includes(columnFilters.cred.toLowerCase())) return false;
                  if (columnFilters.customer && !displayName.toLowerCase().includes(columnFilters.customer.toLowerCase())) return false;
                  if (columnFilters.rateList && !String(customer.rate_list || '').toLowerCase().includes(columnFilters.rateList.toLowerCase())) return false;
                  if (columnFilters.contacts && !String(customer.contacts?.length || 0).includes(columnFilters.contacts)) return false;
                  if (columnFilters.addresses && !String(customer.addresses?.length || 0).includes(columnFilters.addresses)) return false;
                  return true;
                }).map((customer) => (
                  <tr key={customer.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-sm font-medium text-slate-900">{customer.credibility_rating || '-'}</td>
                    <td className="py-3 px-4 text-sm font-medium text-slate-900">{customer.customer_name || customer.addresses?.[0]?.customer_name_child || customer.parent_customer_name}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{customer.rate_list || '-'}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">
                      {customer.contacts?.length || 0}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">
                      {customer.addresses?.length || 0}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <Button
                          data-testid={`view-customer-${customer.id}`}
                          onClick={() => handleView(customer)}
                          variant="ghost"
                          size="sm"
                          className="text-slate-600 hover:text-slate-700 hover:bg-slate-50"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          data-testid={`edit-customer-${customer.id}`}
                          onClick={() => handleEdit(customer)}
                          variant="ghost"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          data-testid={`delete-customer-${customer.id}`}
                          onClick={() => handleDelete(customer.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showModal && (
        <EnhancedCustomerModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingCustomer(null);
          }}
          onSuccess={handleModalSuccess}
          existingCustomer={editingCustomer}
        />
      )}

      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent data-testid="view-customer-modal" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-primary font-semibold">Customer Details</DialogTitle>
          </DialogHeader>
          {viewingCustomer && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Credibility Rating</p>
                  <p className="text-lg font-semibold text-slate-900">{viewingCustomer.credibility_rating || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Customer Name</p>
                  <p className="text-lg font-semibold text-slate-900">{viewingCustomer.customer_name || viewingCustomer.addresses?.[0]?.customer_name_child || viewingCustomer.parent_customer_name}</p>
                </div>
                {viewingCustomer.rate_list && (
                  <div>
                    <p className="text-sm text-slate-500">Rate List</p>
                    <p className="text-lg text-slate-900">{viewingCustomer.rate_list}</p>
                  </div>
                )}
              </div>

              {viewingCustomer.contacts?.length > 0 && (
                <div>
                  <h3 className="text-lg font-primary font-semibold text-slate-900 mb-3">Contacts</h3>
                  <div className="space-y-2">
                    {viewingCustomer.contacts.map((contact, index) => (
                      <div key={index} className="p-3 bg-slate-50 rounded-lg">
                        <p className="font-medium text-slate-900">{contact.name}</p>
                        {contact.designation && (
                          <p className="text-sm text-slate-600">{contact.designation}</p>
                        )}
                        {contact.mobile && (
                          <p className="text-sm text-slate-500">{contact.mobile}</p>
                        )}
                        {contact.email && (
                          <p className="text-sm text-slate-500">{contact.email}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {viewingCustomer.addresses?.length > 0 && (
                <div>
                  <h3 className="text-lg font-primary font-semibold text-slate-900 mb-3">Addresses</h3>
                  <div className="space-y-2">
                    {viewingCustomer.addresses.map((address, index) => (
                      <div key={index} className="p-3 bg-slate-50 rounded-lg">
                        <p className="font-medium text-slate-900">{address.type || 'Address'}</p>
                        <p className="text-sm text-slate-600">
                          {[address.street, address.city, address.state, address.pincode]
                            .filter(Boolean)
                            .join(', ')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {viewingCustomer.bore_details?.length > 0 && (
                <div>
                  <h3 className="text-lg font-primary font-semibold text-slate-900 mb-3">Bore Details</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {viewingCustomer.bore_details.map((bore, index) => (
                      <div key={index} className="p-3 bg-slate-50 rounded-lg">
                        <p className="font-medium text-slate-900">{bore.machine_name}</p>
                        <p className="text-sm text-slate-600">
                          Bore: {bore.bore}mm, Degree: {bore.degree}°
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerManagement;
